// Phase 9.8 — Shared vendor picker.
//
// Fetches once from GET /api/fnb/vendors (module-level cache) so multiple
// <VendorPicker /> instances on the same page share a single HTTP call.
// Uses `vendor_name` as the value type (matches OrderingConfigPatch which
// keys on `vendor_name`); exposes `vendor_id` on the change payload so
// consumers doing rate look-ups can join on `vendor_id` without a second
// fetch.
//
// NOT wired to the 4 existing inlined pickers (FnbPoAutoSuggest,
// FnbPoPurchaseOrders, FnbPoManualCreate, FnbPoOrderingRules) — those
// refactors are a separate phase.

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Module-level cache — shared across every <VendorPicker /> mount for the
// lifetime of the page. Vendors change infrequently; a page refresh clears
// the cache naturally. No TTL — session-scoped is enough.
let _cache = null;
let _inflight = null;

async function _fetchVendors() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await api.get("/fnb/vendors");
      const rows = Array.isArray(res.data) ? res.data : [];
      _cache = rows.filter((v) => v?.active !== false);
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function VendorPicker({
  value,
  onChange,
  placeholder = "Select vendor...",
  disabled = false,
  required = false,
}) {
  const [vendors, setVendors] = useState(_cache);
  const [loading, setLoading] = useState(_cache === null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (_cache) {
      setVendors(_cache);
      setLoading(false);
      return;
    }
    setLoading(true);
    _fetchVendors()
      .then((rows) => {
        if (!cancelled) {
          setVendors(rows);
          setLoading(false);
        }
      })
      .catch((e) => {
        console.error("VendorPicker: failed to load /fnb/vendors", e);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const handleValueChange = (nextName) => {
    const match = (vendors || []).find((v) => v.name === nextName);
    onChange?.({
      name: nextName || null,
      id: match?.vendor_id || null,
    });
  };

  // Effective label the trigger renders. Shadcn Select renders empty
  // when value is null/"" — placeholder shows automatically.
  const triggerValue = value || undefined;

  return (
    <Select
      value={triggerValue}
      onValueChange={handleValueChange}
      disabled={disabled || loading || error}
    >
      <SelectTrigger
        data-testid="vendor-picker-trigger"
        aria-required={required || undefined}
      >
        <SelectValue
          placeholder={
            error
              ? "\u26A0 Failed to load vendors"
              : loading
              ? "Loading vendors..."
              : placeholder
          }
        />
      </SelectTrigger>
      <SelectContent>
        {(vendors || []).map((v) => (
          <SelectItem
            key={v.vendor_id}
            value={v.name}
            data-testid={`vendor-picker-option-${v.vendor_id}`}
          >
            {v.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default VendorPicker;
